// @section 1 WebsiteVisitorsCard — site-wide daily website visitors, human vs scanner.
// Self-contained tile: fetches GET /api/hq/morning-metrics/ga4-daily-site-wide and renders the
// latest settled day's per-host human/scanner split. Forward "website traffic" surface under the
// 2026-06-19 no-links cold-email doctrine — there is no per-email link to attribute through, so this
// is the whole-site read. Renders a precomputed number (it does not derive), degrades silently when
// the endpoint is unconfigured or the table is empty.

import { useEffect, useState } from 'react';
import { getGa4SiteDaily, type Ga4SiteDaily } from '../api';

// @subsection 1.1 Helpers
function fmtDate(d: string | null): string {
  if (!d) return '—';
  return new Date(`${d}T00:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

// @section 2 Component
export default function WebsiteVisitorsCard() {
  const [data, setData] = useState<Ga4SiteDaily | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  // @block 2.1 Load latest settled day (14-day window for the host list context)
  useEffect(() => {
    getGa4SiteDaily(14)
      .then((d) => { setData(d); setError(null); })
      .catch((e) => setError(String(e)))
      .finally(() => setLoaded(true));
  }, []);

  // Secondary tile — stay quiet until loaded, and degrade silently on error / empty.
  if (!loaded || error || !data || !data.latest_date) return null;

  const t = data.totals;
  const humanPct = t.visitors > 0 ? Math.round((100 * t.human) / t.visitors) : 0;
  const topHosts = data.by_host.filter((h) => h.visitors > 0).slice(0, 5);

  // @block 2.2 Render
  return (
    <div className="mb-8">
      <div className="text-[10px] uppercase tracking-[2px] text-muted mb-2">
        Website visitors · {fmtDate(data.latest_date)}
      </div>
      <div className="rounded-lg border border-paper-100/15 bg-ink-900/40 px-4 py-3">
        <div className="flex items-baseline gap-3 mb-2">
          <span className="text-2xl font-bold text-ok tabular-nums">{t.human}</span>
          <span className="text-sm text-muted">human</span>
          <span className="text-paper-100/30">·</span>
          <span className="text-lg font-semibold text-paper-100/50 tabular-nums">{t.scanner}</span>
          <span className="text-sm text-muted">scanner</span>
          <span className="text-xs text-muted ml-auto">{humanPct}% human of {t.visitors}</span>
        </div>
        {topHosts.length > 0 && (
          <div className="space-y-1 border-t border-paper-100/10 pt-2">
            {topHosts.map((h) => (
              <div key={h.site_host} className="flex items-center text-xs">
                <span className="text-paper-100/70 truncate">{h.site_host}</span>
                <span className="ml-auto tabular-nums">
                  <span className="text-ok">{h.human}</span>
                  <span className="text-muted"> / {h.visitors}</span>
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
